import type {
  StripePaymentContext,
  StripePaymentTarget,
  StripeTransactionStatusDto,
} from "../types/api/stripe";
import api from "./api";
import {
  completeStripePayment,
  extractStripeTransactionStatus,
  isRetryableStripePaymentError,
  StripePaymentPendingError,
  StripePaymentTargetError,
  StripePaymentTerminalError,
  stripeTransactionMatchesTarget,
} from "./stripe";

type StripePaymentApi = Pick<
  typeof api,
  "purchase" | "purchase_all" | "recharge" | "stripe_transactions"
>;

export const STRIPE_STATUS_POLL_DELAYS_MS = [0, 1_000, 2_000, 3_000, 5_000];

const wait = (delay: number) =>
  new Promise<void>((resolve) => window.setTimeout(resolve, delay));

const targetForContext = (
  context: StripePaymentContext,
): StripePaymentTarget => {
  if (context.flow === "purchase") {
    return {
      flow: "purchase",
      consolidationId: context.consolidationId,
    };
  }
  if (context.flow === "purchase_all") {
    return { flow: "purchase_all", signId: context.signId };
  }
  return { flow: context.flow };
};

export const completeStripePaymentForContext = (
  context: StripePaymentContext,
  paymentIntentId: string,
  paymentApi: StripePaymentApi = api,
) => {
  switch (context.flow) {
    case "purchase":
      return completeStripePayment(() =>
        paymentApi.purchase.completeStripePaymentIntent(
          context.consolidationId,
          { payment_intent_id: paymentIntentId },
        ),
      );
    case "purchase_all":
      return completeStripePayment(() =>
        paymentApi.purchase_all.complete_stripe_payment_intent({
          sign_id: context.signId,
          payment_intent_id: paymentIntentId,
        }),
      );
    case "recharge":
      return completeStripePayment(() =>
        paymentApi.recharge.rechargeStripeCompletePaymentIntent({
          payment_intent_id: paymentIntentId,
        }),
      );
  }
};

export const waitForStripePayment = async (
  paymentIntentId: string,
  target: StripePaymentTarget,
  paymentApi: StripePaymentApi = api,
  pollDelays: number[] = STRIPE_STATUS_POLL_DELAYS_MS,
): Promise<StripeTransactionStatusDto> => {
  for (const delay of pollDelays) {
    if (delay > 0) await wait(delay);

    try {
      const response =
        await paymentApi.stripe_transactions.get(paymentIntentId);
      const transaction = extractStripeTransactionStatus(response);

      if (!stripeTransactionMatchesTarget(transaction, target)) {
        throw new StripePaymentTargetError();
      }
      if (transaction.fulfillment_status === "fulfilled") return transaction;
      if (
        transaction.fulfillment_status === "requires_review" ||
        transaction.payment_status === "failed" ||
        transaction.payment_status === "canceled"
      ) {
        throw new StripePaymentTerminalError(
          transaction.fulfillment_status === "requires_review"
            ? "requires_review"
            : transaction.payment_status,
        );
      }
    } catch (error) {
      if (
        error instanceof StripePaymentTargetError ||
        error instanceof StripePaymentTerminalError
      ) {
        throw error;
      }
      if (!isRetryableStripePaymentError(error)) throw error;
    }
  }

  throw new StripePaymentPendingError();
};

export const finalizeStripePaymentForContext = async (
  context: StripePaymentContext,
  paymentIntentId: string,
  paymentApi: StripePaymentApi = api,
) => {
  try {
    return await completeStripePaymentForContext(
      context,
      paymentIntentId,
      paymentApi,
    );
  } catch (_completionError) {
    return waitForStripePayment(
      paymentIntentId,
      targetForContext(context),
      paymentApi,
    );
  }
};
